import * as React from 'react';
import {useCallback, useEffect, useState} from 'react';
import {SubmitHandler, useForm} from "react-hook-form";
import {useTranslation} from 'react-i18next';
import {useRouter} from "next/router"
import {usePathname} from "next/navigation";
import SearchIcon from '@mui/icons-material/Search';
import ClearIcon from '@mui/icons-material/Clear';
import {InputAdornment, TextField} from "@mui/material";
import IconButton from "@mui/material/IconButton";
import routes from "../../../common/constants/routes";
import {getRouteUrl} from "../../../utility/navigationUtil";
import {usePageContext} from "../../../context/PageContext";


export interface Props {
    fullWidth?: boolean
    isInContent?: boolean
    placeholder?: string
    reloadFunction?: any
    searchRoute?: string
    sortType?: string
    val?: string
    withResetButton?: boolean
    onSearchSubmit?: () => void
    customInputProps?: any
    dataCypress?: string
}

type FormValues = {
    search: string
}


function C036SearchField({isInContent, placeholder, reloadFunction, searchRoute, sortType, val, withResetButton, onSearchSubmit, customInputProps, dataCypress}: Props) {
    const {searchEnabled, rootUrl} = usePageContext();
    const pathname = usePathname();
    const init = val || '';
    const size = isInContent ? 'medium' : 'small';
    const router = useRouter();
    const {t} = useTranslation();
    const {register, handleSubmit} = useForm<FormValues>();
    const [search, setSearch] = useState(init);
    const [showSearchInNavi, setShowSearchInNavi] = useState(true);
    const fallbackSearchUrl = getRouteUrl(rootUrl, routes.SEARCH_RESULTS);
    const route = searchRoute || fallbackSearchUrl;

    const inputStyle = {
        backgroundColor: 'white',
        color: 'rgb(51, 51, 51)',
        fontFamily: 'HelveticaNeueLTStd-Roman, Helvetica, Arial, sans-serif',
        fontSize: '0.9rem',
        fontWeight: '400',
        letterSpacing: '0.15px',
        lineHeight: '1.4375em'
    }

    const handleChange = useCallback((event: any) => {
        const val = event.target.value;
        setSearch(val);
    }, []);


    useEffect(() => {
        setSearch(init);
    }, [init]);

    useEffect(() => {
        if (sortType) {
            handleSearch();
        }
    }, [sortType, handleChange]);

    useEffect(() => {
        if (pathname?.indexOf(routes.SEARCH_RESULTS) > -1 || (!searchEnabled)) {
            setShowSearchInNavi(false);
        } else {
            setShowSearchInNavi(true);
        }
    }, [pathname, searchEnabled]);

    function handleSearch() {
        if (search?.length > 0 || sortType) {
            const appendSymbol = route.includes("?") ? "&": "?";
            const forwardUrl = sortType ? `${route}${appendSymbol}search=${search}&sorting=${sortType}` : `${route}${appendSymbol}search=${search}`;
            router.push(forwardUrl);
            if (reloadFunction) {
                reloadFunction(search);
            }
        }
        if (onSearchSubmit) {
            onSearchSubmit();
        }
    }

    const handleReset = () => {
        setSearch('');
        router.push(route);
        if (reloadFunction) {
            reloadFunction('');
        }
    };

    function catchEnter(e: any) {
        if (e.key === 'Enter') {
            e.preventDefault();
            handleSearch();
        }
    }

    const onSubmit: SubmitHandler<FormValues> = () => handleSearch();

    if (!isInContent && !showSearchInNavi) {
        return null;
    }

    return (
        <form onSubmit={() => handleSubmit(onSubmit)}>
            <TextField
                data-cypress={dataCypress}
                type="text"
                style={inputStyle}
                value={search}
                {...register('search')}
                placeholder={placeholder || t('c036_search') || ''}
                inputProps={{
                    'aria-label': t('c036_search') ?? '',
                    'data-cypress': 'searchField',
                    ...customInputProps
                }}
                size={size}
                onKeyDown={(e) => {
                    catchEnter(e);
                }}
                fullWidth={true}
                onChange={handleChange}
                InputProps={{
                    endAdornment: (
                        <InputAdornment position="end">
                            {withResetButton && <IconButton tabIndex={-1} data-cypress='searchResetButton' onClick={handleReset}>
                                <ClearIcon fontSize="small" sx={{opacity: 0.42}}/>
                            </IconButton>
                            }
                            <IconButton tabIndex={-1} data-cypress="searchButton" onClick={handleSearch}>
                                <SearchIcon/>
                            </IconButton>
                        </InputAdornment>
                    ),
                }}
            />
        </form>
    );
}

export default C036SearchField;
